import React, { useEffect, useState } from 'react';
import { 
  Grid, 
  CircularProgress, 
  Box, 
  useMediaQuery, 
  useTheme,
  Paper,
  Typography,
  IconButton,
  AppBar,
  Toolbar,
  Fab,
  SwipeableDrawer,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  Button,
  InputBase,
  Divider,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Chat as ChatIcon,
  People as PeopleIcon
} from '@mui/icons-material';

import { useSelector } from 'react-redux';
import moment from 'moment';

import { useGetUserChatListQuery } from '../../redux/thunks/users/userchatlist';
import { useLazyGetUserChatHistoryQuery } from '../../redux/thunks/users/userchathistory';
import { useWebSocket } from '../../WebSockets/WebSocketProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';

// Import the existing components
import ChatMiddleColumn from './ChatMiddleColumn';
import ChatDetailColumn from './ChatDetailColumn';
import ChatMessageDetail from './ChatMessageDetail';

// 🆕 Mobile Chat List Component
const MobileChatList = ({ 
  conversations, 
  potentialContacts, 
  onUserClick, 
  selectedUser, 
  onStartConversation, 
  onFindPeople 
}) => {
  const [activeTab, setActiveTab] = useState('conversations');

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={activeTab === 'conversations' ? 'contained' : 'text'}
            size="small"
            startIcon={<ChatIcon />}
            onClick={() => setActiveTab('conversations')}
            sx={{ flex: 1 }}
          >
            Chats ({conversations.length})
          </Button>
          <Button
            variant={activeTab === 'contacts' ? 'contained' : 'text'}
            size="small"
            startIcon={<PeopleIcon />}
            onClick={() => setActiveTab('contacts')}
            sx={{ flex: 1 }}
          >
            Contacts ({potentialContacts.length})
          </Button>
        </Box>
      </Box>

      {/* Search Bar */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 1, 
          mb: 2, 
          display: 'flex', 
          alignItems: 'center',
          border: 'none',
          bgcolor: 'grey.50'
        }}
      >
        <SearchIcon color="action" sx={{ mr: 1 }} />
        <InputBase
          placeholder="Search conversations..."
          fullWidth
          size="small"
        />
      </Paper>

      {/* Content based on active tab */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 'conversations' ? (
          <>
            {conversations.length > 0 ? (
              <List sx={{ p: 0 }}>
                {conversations.map((user, index) => (
                  <ListItem
                    key={user.otherUserId || index}
                    onClick={() => onUserClick(user)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: selectedUser === user.otherUserId ? 'primary.light' : 'transparent',
                      '&:hover': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        color="success"
                        variant="dot"
                        invisible={!user.isOnline}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                      >
                        <Avatar
                          src={user.photoUrl}
                          alt={user.displayName}
                          sx={{ width: 50, height: 50 }}
                        >
                          {user.displayName?.charAt(0)?.toUpperCase()}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" noWrap>
                          {user.displayName}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {user.lastMessage || 'No messages yet'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.formattedMsgTime}
                          </Typography>
                        </Box>
                      }
                    />
                    {user.unreadCount > 0 && (
                      <Chip
                        label={user.unreadCount}
                        color="primary"
                        size="small"
                        sx={{ minWidth: 24, height: 24 }}
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            ) : (
              <Card sx={{ textAlign: 'center', py: 4 }}>
                <CardContent>
                  <ChatIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No conversations yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Start chatting with your network connections
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={onStartConversation}
                  >
                    Start Conversation
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <>
            {potentialContacts.length > 0 ? (
              <List sx={{ p: 0 }}>
                {potentialContacts.map((contact, index) => (
                  <ListItem
                    key={contact.otherUserId || index}
                    onClick={() => onUserClick(contact)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: 'transparent',
                      '&:hover': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={contact.photoUrl}
                        alt={contact.displayName}
                        sx={{ width: 50, height: 50 }}
                      >
                        {contact.displayName?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" noWrap>
                          {contact.displayName}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {contact.lastMessage}
                        </Typography>
                      }
                    />
                    <IconButton size="small" color="primary">
                      <ChatIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Card sx={{ textAlign: 'center', py: 4 }}>
                <CardContent>
                  <PeopleIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No network contacts
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Build your network to start conversations
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={onFindPeople}
                  >
                    Find People
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Box>

      {/* Quick Actions */}
      <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={onFindPeople}
          fullWidth
          sx={{ mb: 1 }}
        >
          Find People
        </Button>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={onStartConversation}
          fullWidth
        >
          New Conversation
        </Button>
      </Box>
    </Box>
  );
};

// 🆕 Mobile Chat Header Component
const MobileChatHeader = ({ selectedUser, onBackToUserList, onOpenUserList }) => {
  return (
    <AppBar position="static" color="default" elevation={1}>
      <Toolbar sx={{ minHeight: '56px !important' }}>
        <IconButton
          edge="start"
          onClick={onBackToUserList}
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        
        {selectedUser && (
          <>
            <Avatar
              src={selectedUser.photoUrl}
              alt={selectedUser.displayName}
              sx={{ width: 36, height: 36, mr: 2 }}
            >
              {selectedUser.displayName?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" noWrap>
                {selectedUser.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedUser.isOnline ? 'Online' : 'Last seen recently'}
              </Typography>
            </Box>
          </>
        )}
        
        <IconButton
          edge="end"
          onClick={onOpenUserList}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};

// 🆕 Mobile Chat Sidebar Component
const MobileChatSidebar = ({ 
  isOpen, 
  onClose,
  conversations,
  potentialContacts,
  onUserClick,
  selectedUser,
  onStartConversation,
  onFindPeople
}) => {
  return (
    <SwipeableDrawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: { 
          width: '100%',
          maxWidth: 400,
          bgcolor: 'background.default'
        }
      }}
    >
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Conversations
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, height: 'calc(100vh - 64px)', overflow: 'auto' }}>
        <MobileChatList
          conversations={conversations}
          potentialContacts={potentialContacts}
          onUserClick={(user) => {
            onUserClick(user);
            onClose();
          }}
          selectedUser={selectedUser}
          onStartConversation={() => {
            onStartConversation();
            onClose();
          }}
          onFindPeople={() => {
            onFindPeople();
            onClose();
          }}
        />
      </Box>
    </SwipeableDrawer>
  );
};

// 🆕 Enhanced Main Chat Component
const Chat = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { authStatus } = useAuthenticator(context => [context.authStatus]);

  // Mobile-specific state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChatList, setShowChatList] = useState(true);

  const otherUser = location.state?.otherUser || '';

  const [selectedUser, setSelectedUser] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [isUserListVisible, setIsUserListVisible] = useState(true);
  const [chatListWithInitials, setChatListWithInitials] = useState([]);
  const [potentialContacts, setPotentialContacts] = useState([]);

  const userId = useSelector((state) => state.auth?.userId);
  const displayName = useSelector((state) => state.auth.user?.displayName);
  const profileImageUrl = useSelector((state) => state.auth?.user?.profileImageUrl);
  const token = useSelector((state) => state.auth?.token || null);
  const firstDegrees = useSelector((state) => state.auth?.user?.firstDegreeFriends || []);
  const myCurrentNetworkList = useSelector((state) => state.auth?.user?.myCurrentNetworkList || []);
  const authUser = useSelector((state) => state.auth?.user);

  const isAuthenticated = authStatus === 'authenticated' && token;

  const { data: userChatList, isLoading: chatListLoading } = useGetUserChatListQuery(userId, {
    pollingInterval: 200000,
    skip: !isAuthenticated
  });

  const [fetchChatHistory, { data: chatHistoryData, isLoading: chatHistoryLoading }] = useLazyGetUserChatHistoryQuery();
  const { socket } = useWebSocket();

  // Existing useEffect hooks remain the same
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth/signin', { state: {name: 'chat', link: `/chat`}});
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    console.log('=== CHAT HISTORY DATA EFFECT ===');
    console.log('chatHistoryData:', chatHistoryData);
    console.log('chatHistoryLoading:', chatHistoryLoading);
    console.log('selectedUser:', selectedUser);
    
    if (chatHistoryData && chatHistoryData.messages) {
      console.log('Setting chat history from chatHistoryData:', chatHistoryData.messages);
      setChatHistory(chatHistoryData.messages);
    } else if (chatHistoryData && !chatHistoryData.messages) {
      console.log('Chat history data exists but no messages:', chatHistoryData);
      setChatHistory([]);
    }
  }, [chatHistoryData, chatHistoryLoading]);

  useEffect(() => {
    const handleIncomingMessage = (event) => {
      const socketData = JSON.parse(event.data);
      if (socketData.body.type === 'CHAT_MESSAGE' && socketData.body.subType === 'RECEIVE') {
        const newMessage = transformWebSocketDataForRendering(socketData.body);
        
        if (newMessage.sender !== userId) {
          setChatHistory((prevChatHistory) => [...prevChatHistory, newMessage]);
        }
      }
    };

    if (socket) {
      socket.addEventListener('message', handleIncomingMessage);
    }

    return () => {
      if (socket) {
        socket.removeEventListener('message', handleIncomingMessage);
      }
    };
  }, [socket, userId]);

  // Mobile-specific useEffect for responsive behavior
  useEffect(() => {
    if (isMobile && selectedUser) {
      setShowChatList(false);
    } else if (!isMobile) {
      setShowChatList(true);
    }
  }, [isMobile, selectedUser]);

  // Auto-select otherUser when navigating from profile
  useEffect(() => {
    const autoSelectUser = async () => {
      if (otherUser && otherUser.otherUserId && isAuthenticated && !chatListLoading && !selectedUser) {
        console.log('Auto-selecting otherUser from navigation:', otherUser);
        
        setSelectedUser(otherUser);
        
        if (isMobile) {
          setIsUserListVisible(false);
          setShowChatList(false);
        }
        
        setTimeout(async () => {
          try {
            console.log('Fetching chat history for otherUser:', otherUser.otherUserId);
            const result = await fetchChatHistory(otherUser.otherUserId);
            console.log('fetchChatHistory result:', result);
            
            if (result?.data?.messages) {
              console.log('Setting chat history directly from result:', result.data.messages);
              setChatHistory(result.data.messages);
            }
          } catch (error) {
            console.error('Error fetching chat history for otherUser:', error);
          }
        }, 100);
      }
    };
  
    autoSelectUser();
  }, [otherUser, selectedUser, isAuthenticated, chatListLoading, isMobile, fetchChatHistory]);

  // Process chat list with formatted time and network connections
  useEffect(() => {
    if (userChatList) {
      const updatedList = userChatList.map((user) => ({
        ...user,        
        formattedMsgTime: moment(user.msgTime).format('MMM-YYYY'),         
      }));
      
      if (otherUser) {
        const otherUserExists = updatedList.some((user) => user.otherUserId === otherUser?.otherUserId);
        if(!otherUserExists) {
          updatedList.unshift({
            ...otherUser,         
            formattedMsgTime: moment().format('MMM-YYYY'),
          });
        }
      }
      setChatListWithInitials(updatedList);
    }

    console.log('=== CHAT DEBUG ===');
    console.log('Full auth user object:', authUser);
    console.log('Available user keys:', authUser ? Object.keys(authUser) : 'No user object');
    console.log('firstDegrees:', firstDegrees);
    console.log('myCurrentNetworkList:', myCurrentNetworkList);
    console.log('firstDegrees length:', firstDegrees?.length || 0);
    console.log('myCurrentNetworkList length:', myCurrentNetworkList?.length || 0);

    const networkConnections = [...(firstDegrees || []), ...(myCurrentNetworkList.userDetails || [])];
    
    console.log('Combined networkConnections:', networkConnections);
    console.log('Combined networkConnections length:', networkConnections.length);
    
    if (networkConnections.length > 0) {
      const uniqueContacts = networkConnections
        .filter((contact, index, self) => 
          index === self.findIndex(c => c.userId === contact.userId)
        )
        .map((contact) => ({
          otherUserId: contact.userId,
          displayName: contact.displayName || contact.name,
          photoUrl: contact.profileImageUrl || contact.photoUrl,
          lastMessage: "Start a conversation...",
          msgTime: new Date(),
          formattedMsgTime: "New",
          isNetworkContact: true
        }));
      
      console.log('uniqueContacts after processing:', uniqueContacts);
      setPotentialContacts(uniqueContacts);
    } else {
      console.log('No network connections found, setting empty array');
      setPotentialContacts([]);
    }
  }, [userChatList, otherUser, firstDegrees, myCurrentNetworkList, authUser]);

  const transformWebSocketDataForRendering = (socketData) => {
    const { sender, text, displayName, profileImageUrl, msgId } = JSON.parse(socketData.data);
    return {
      msgId,
      text,
      msgTime: new Date(),
      profileImageUrl,
      displayName,
      sender,
    };
  };

  const handleUserClick = async (user) => {
    console.log('=== USER CLICK ===');
    console.log('Clicked user:', user);
    
    setSelectedUser(user);
    
    if (isMobile) {
      setIsUserListVisible(false);
      setShowChatList(false);
    }
    
    setChatHistory([]);
    
    try {
      console.log('Fetching chat history for clicked user:', user.otherUserId);
      const result = await fetchChatHistory(user.otherUserId);
      console.log('fetchChatHistory result for clicked user:', result);
    } catch (error) {
      console.error('Error fetching chat history for clicked user:', error);
    }
  };

  const handleBackToUserList = () => {
    if (isMobile) {
      setShowChatList(true);
      setSelectedUser(null);
    }
    setIsUserListVisible(true);
  };

  const handleMessageChange = (e) => {
    setMessageInput(e.target.value);
  };

  const handleSendMessage = () => {
    if (messageInput.trim() !== '') {
      const ephemeralNewMessage = {
        text: messageInput,
        displayName,
        profileImageUrl,
        msgId: (Math.floor(100000 + Math.random() * 900000)).toString(),
        msgTime: new Date(),
      };
      setChatHistory((prevChatHistory) => [...prevChatHistory, ephemeralNewMessage]);

      if (socket) {
        const payload = {
          type: 'CHAT_MESSAGE',
          subType: 'SEND',
          data: {
            sender: userId,
            to: selectedUser.otherUserId,
            text: messageInput,
          },
        };
        socket.send(JSON.stringify(payload));
      }

      setMessageInput('');
    }
  };

  const handleFindPeople = () => {
    navigate('/usersearch');
  };

  const handleStartConversation = () => {
    navigate('/usersearch');
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (chatListLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box maxWidth={1400} mx="auto" p={isMobile ? 0 : 2}>
      <Paper 
        elevation={isMobile ? 0 : 1}
        sx={{ 
          border: isMobile ? 'none' : '1px solid #e0e0e0', 
          marginTop: isMobile ? 0 : '20px',
          borderRadius: isMobile ? 0 : 1,
          overflow: 'hidden'
        }}
      >
        {isMobile ? (
          // 🆕 Mobile Layout
          <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {showChatList ? (
              // Mobile Chat List View
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <AppBar position="static" color="default" elevation={1}>
                  <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      Messages
                    </Typography>
                    <IconButton onClick={() => setSidebarOpen(true)}>
                      <MenuIcon />
                    </IconButton>
                  </Toolbar>
                </AppBar>
                
                <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                  <MobileChatList
                    conversations={chatListWithInitials || []}
                    potentialContacts={potentialContacts || []}
                    onUserClick={handleUserClick}
                    selectedUser={selectedUser?.otherUserId}
                    onStartConversation={handleStartConversation}
                    onFindPeople={handleFindPeople}
                  />
                </Box>
              </Box>
            ) : (
              // Mobile Chat View
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <MobileChatHeader
                  selectedUser={selectedUser}
                  onBackToUserList={handleBackToUserList}
                  onOpenUserList={() => setSidebarOpen(true)}
                />
                
                <Box sx={{ flex: 1 }}>
                  <ChatDetailColumn
                    selectedUser={selectedUser}
                    chatHistory={chatHistory}
                    messageInput={messageInput}
                    onMessageChange={handleMessageChange}
                    onSendMessage={handleSendMessage}
                    onBackToUserList={handleBackToUserList}
                    onFindPeople={handleFindPeople}
                    isMobile={isMobile}
                    ChatMessageDetail={ChatMessageDetail}
                  />
                </Box>
              </Box>
            )}

            {/* Mobile Sidebar */}
            <MobileChatSidebar
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              conversations={chatListWithInitials || []}
              potentialContacts={potentialContacts || []}
              onUserClick={handleUserClick}
              selectedUser={selectedUser?.otherUserId}
              onStartConversation={handleStartConversation}
              onFindPeople={handleFindPeople}
            />

            {/* Floating Action Button for quick access */}
            {!showChatList && (
              <Fab
                color="primary"
                sx={{ 
                  position: 'fixed', 
                  bottom: 80, 
                  right: 16,
                  zIndex: 1000
                }}
                onClick={() => setSidebarOpen(true)}
              >
                <ChatIcon />
              </Fab>
            )}
          </Box>
        ) : (
          // 🆕 Desktop Layout - Original 2 Columns (unchanged)
          <Grid 
            container 
            sx={{ minHeight: '600px' }}
          >
            {/* Left Column - Chat List */}
            {(!isMobile || (isMobile && isUserListVisible)) && (
              <Grid 
                item 
                xs={12} 
                md={5} 
                lg={4}
                sx={{ 
                  p: 2, 
                  bgcolor: '#f8f9fa',
                  borderRight: { md: '1px solid #e0e0e0' },
                  minHeight: '500px'
                }}
              >
                <ChatMiddleColumn
                  conversations={chatListWithInitials || []}
                  potentialContacts={potentialContacts || []}
                  onStartConversation={handleStartConversation}
                  onFindPeople={handleFindPeople}
                  onUserClick={handleUserClick}
                  selectedUser={selectedUser?.otherUserId}
                  otherUser={otherUser}
                />
              </Grid>
            )}

            {/* Right Column - Chat Messages */}
            {(!isMobile || (isMobile && !isUserListVisible)) && (
              <Grid 
                item 
                xs={12} 
                md={7} 
                lg={8}
                sx={{ 
                  p: 0, 
                  bgcolor: 'white'
                }}
              >
                <ChatDetailColumn
                  selectedUser={selectedUser}
                  chatHistory={chatHistory}
                  messageInput={messageInput}
                  onMessageChange={handleMessageChange}
                  onSendMessage={handleSendMessage}
                  onBackToUserList={handleBackToUserList}
                  onFindPeople={handleFindPeople}
                  isMobile={isMobile}
                  ChatMessageDetail={ChatMessageDetail}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default Chat;